import type { StoragePort } from '@dcc/rules/persistence/storagePort.ts';
export interface ScratchNote { id: string; title: string; text: string; }
export interface Scratchpad { kind: 'crawler-scratchpad'; version: 1; characterId: string; tabs: ScratchNote[]; }
export class ScratchpadConflict extends Error {}
export const scratchpadKey = (id: string) => `scratchpad-${id}`;
export const emptyScratchpad = (id: string): Scratchpad => ({ kind: 'crawler-scratchpad', version: 1, characterId: id, tabs: [] });
export function readScratchpad(raw: unknown, id: string): Scratchpad {
  if (raw === null) return emptyScratchpad(id);
  if (!raw || typeof raw !== 'object') throw new Error('These notes cannot be read. The saved document has been preserved.');
  const value = raw as Scratchpad;
  if (value.kind !== 'crawler-scratchpad' || value.version !== 1 || value.characterId !== id || !Array.isArray(value.tabs) || value.tabs.length > 32) throw new Error('These notes use an unsupported format. The saved document has been preserved.');
  const ids = new Set<string>();
  for (const tab of value.tabs) {
    if (!tab || typeof tab.id !== 'string' || !tab.id || tab.id.length > 128 || ids.has(tab.id) || typeof tab.title !== 'string' || !tab.title.trim() || tab.title.length > 80 || typeof tab.text !== 'string' || tab.text.length > 20000) throw new Error('These notes contain an invalid tab. The saved document has been preserved.');
    ids.add(tab.id);
  }
  if (new TextEncoder().encode(JSON.stringify(value)).byteLength > 200000) throw new Error('Keep this scratchpad below 200 kB of text.');
  return structuredClone(value);
}
export async function saveScratchpad(storage: StoragePort, base: Scratchpad, draft: Scratchpad): Promise<Scratchpad> {
  const next = readScratchpad(draft, base.characterId);
  const current = readScratchpad(await storage.readCustomContent(scratchpadKey(base.characterId)), base.characterId);
  if (JSON.stringify(current) !== JSON.stringify(base)) throw new ScratchpadConflict('The saved notes changed elsewhere. Your draft is still here; reload saved notes before saving again.');
  await storage.writeCustomContent(scratchpadKey(base.characterId), next);
  return next;
}
